// GPN Design System Theme - Dark Mode (Glassmorphism) & Light Mode
import { createTheme } from '@mui/material/styles';

const sharedTypography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    '"Fira Sans"',
    '"Droid Sans"',
    '"Helvetica Neue"',
    'sans-serif',
  ].join(','),
  button: {
    textTransform: 'none',
    fontWeight: 600,
  },
};

const darkThemeConfig = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#4CA1AF',
      dark: '#3A8A97',
      light: '#6BB5C1',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2C3E50',
      dark: '#1A252F',
      light: '#4A5F7A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: 'linear-gradient(135deg, #1a1f2e 0%, #2C3E50 100%)',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.4)',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: '#4CA1AF',
      light: '#6BB5C1',
      dark: '#3A8A97',
    },
    divider: 'rgba(255, 255, 255, 0.1)',
  },
  typography: {
    ...sharedTypography,
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.3, color: '#FFFFFF' },
    h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, color: '#FFFFFF' },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, color: '#FFFFFF' },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.3, color: '#FFFFFF' },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.3, color: '#FFFFFF' },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3, color: '#FFFFFF' },
    body1: { fontSize: '0.875rem', lineHeight: 1.6, color: '#FFFFFF' },
    body2: { fontSize: '0.8125rem', lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.7)' },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 4px 12px rgba(76, 161, 175, 0.15)',
    '0 8px 16px rgba(76, 161, 175, 0.2)',
    '0 12px 24px rgba(76, 161, 175, 0.25)',
    '0 16px 32px rgba(76, 161, 175, 0.3)',
    '0 20px 40px rgba(76, 161, 175, 0.35)',
    ...Array(19).fill('0 24px 48px rgba(76, 161, 175, 0.4)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(76, 161, 175, 0.3)',
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #4CA1AF 0%, #3A8A97 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5BB1BF 0%, #4A9AA7 100%)',
            boxShadow: '0 12px 24px rgba(76, 161, 175, 0.4)',
          },
        },
        outlined: {
          borderWidth: '2px',
          borderColor: 'rgba(76, 161, 175, 0.5)',
          color: '#4CA1AF',
          backdropFilter: 'blur(10px)',
          background: 'rgba(76, 161, 175, 0.05)',
          '&:hover': {
            borderWidth: '2px',
            borderColor: '#4CA1AF',
            background: 'rgba(76, 161, 175, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 300ms ease-in-out',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(76, 161, 175, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4CA1AF',
              borderWidth: '2px',
              boxShadow: '0 0 0 3px rgba(76, 161, 175, 0.1)',
            },
            '& input': {
              color: '#FFFFFF',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.75)',
            fontSize: '0.875rem',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&.MuiInputLabel-shrink': {
              color: '#FFFFFF',
              fontWeight: 600,
              backgroundColor: '#1e293b',
              padding: '1px 8px',
              borderRadius: '4px',
            },
            '&.Mui-focused': {
              color: '#FFFFFF !important',
              fontWeight: 700,
              backgroundColor: '#1e293b',
              padding: '1px 8px',
              borderRadius: '4px',
              boxShadow: '0 0 8px rgba(76, 161, 175, 0.35)',
            },
            '& .MuiFormLabel-asterisk': {
              color: '#F87171',
              fontWeight: 700,
              marginLeft: '3px',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.75)',
          fontSize: '0.875rem',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&.MuiInputLabel-shrink': {
            color: '#FFFFFF',
            fontWeight: 600,
            backgroundColor: '#1e293b',
            padding: '1px 8px',
            borderRadius: '4px',
          },
          '&.Mui-focused': {
            color: '#FFFFFF !important',
            fontWeight: 700,
            backgroundColor: '#1e293b',
            padding: '1px 8px',
            borderRadius: '4px',
            boxShadow: '0 0 8px rgba(76, 161, 175, 0.35)',
          },
          '& .MuiFormLabel-asterisk': {
            color: '#F87171',
            fontWeight: 700,
            marginLeft: '3px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(76, 161, 175, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4CA1AF',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          transition: 'all 300ms ease-in-out',
        },
        elevation0: {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(76, 161, 175, 0.3)',
            borderColor: 'rgba(76, 161, 175, 0.3)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          padding: '16px',
          color: '#FFFFFF',
        },
        head: {
          background: 'rgba(76, 161, 175, 0.1)',
          backdropFilter: 'blur(10px)',
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: '0.8125rem',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          borderBottom: '2px solid rgba(76, 161, 175, 0.3)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 200ms ease-in-out',
          '&:hover': {
            background: 'rgba(76, 161, 175, 0.08)',
            backdropFilter: 'blur(10px)',
          },
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          background: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '4px 0 24px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(76, 161, 175, 0.15)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
  },
};

const lightThemeConfig = {
  palette: {
    mode: 'light',
    primary: {
      main: '#0D9488',
      dark: '#0F766E',
      light: '#14B8A6',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4F46E5',
      dark: '#3730A3',
      light: '#6366F1',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F1F5F9',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#334155',
      disabled: '#64748B',
    },
    success: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857',
    },
    warning: {
      main: '#D97706',
      light: '#F59E0B',
      dark: '#B45309',
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
    },
    info: {
      main: '#0284C7',
      light: '#38BDF8',
      dark: '#0369A1',
    },
    divider: '#CBD5E1',
  },
  typography: {
    ...sharedTypography,
    h1: { fontSize: '2rem', fontWeight: 800, lineHeight: 1.3, color: '#0F172A' },
    h2: { fontSize: '1.65rem', fontWeight: 800, lineHeight: 1.3, color: '#0F172A' },
    h3: { fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.3, color: '#0F172A' },
    h4: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3, color: '#0F172A' },
    h5: { fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.3, color: '#0F172A' },
    h6: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.3, color: '#0F172A' },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6, color: '#0F172A', fontWeight: 500 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6, color: '#334155', fontWeight: 500 },
    caption: { fontSize: '0.82rem', lineHeight: 1.5, color: '#334155', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 4px rgba(15, 23, 42, 0.08)',
    '0 2px 8px rgba(15, 23, 42, 0.08)',
    '0 4px 14px rgba(15, 23, 42, 0.10)',
    '0 8px 24px rgba(15, 23, 42, 0.12)',
    '0 12px 32px rgba(15, 23, 42, 0.14)',
    ...Array(19).fill('0 20px 40px rgba(15, 23, 42, 0.16)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 18px',
          fontSize: '0.875rem',
          fontWeight: 700,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(13, 148, 136, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
          color: '#FFFFFF',
          fontWeight: 700,
          letterSpacing: '0.2px',
          boxShadow: '0 2px 8px rgba(13, 148, 136, 0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0F766E 0%, #115E59 100%)',
            boxShadow: '0 6px 18px rgba(13, 148, 136, 0.45)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: '#0D9488',
          color: '#0D9488',
          background: '#F0FDFA',
          fontWeight: 700,
          '&:hover': {
            borderWidth: '1.5px',
            borderColor: '#0F766E',
            color: '#0F766E',
            background: '#CCFBF1',
            boxShadow: '0 2px 8px rgba(13, 148, 136, 0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 200ms ease-in-out',
            background: '#FFFFFF',
            '& fieldset': {
              borderColor: '#64748B',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: '#0D9488',
              borderWidth: '1.5px',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0D9488',
              borderWidth: '2px',
              boxShadow: '0 0 0 3px rgba(13, 148, 136, 0.15)',
            },
            '& input': {
              color: '#0F172A',
              fontWeight: 600,
            },
          },
          '& .MuiInputLabel-root': {
            color: '#1E293B',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&.MuiInputLabel-shrink': {
              color: '#0D9488',
              fontWeight: 700,
              backgroundColor: '#FFFFFF',
              padding: '1px 8px',
              borderRadius: '4px',
            },
            '&.Mui-focused': {
              color: '#0D9488 !important',
              fontWeight: 700,
              backgroundColor: '#FFFFFF',
              padding: '1px 8px',
              borderRadius: '4px',
            },
            '& .MuiFormLabel-asterisk': {
              color: '#DC2626',
              fontWeight: 700,
              marginLeft: '3px',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#1E293B',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&.MuiInputLabel-shrink': {
            color: '#0D9488',
            fontWeight: 700,
            backgroundColor: '#FFFFFF',
            padding: '1px 8px',
            borderRadius: '4px',
          },
          '&.Mui-focused': {
            color: '#0D9488 !important',
            fontWeight: 700,
            backgroundColor: '#FFFFFF',
            padding: '1px 8px',
            borderRadius: '4px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#64748B',
            borderWidth: '1.5px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0D9488',
            borderWidth: '1.5px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0D9488',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          background: '#FFFFFF',
          border: '1.5px solid #CBD5E1',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.07)',
          transition: 'all 200ms ease-in-out',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.07)',
        },
        elevation2: {
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.10)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          background: '#FFFFFF',
          border: '1.5px solid #CBD5E1',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.07)',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            borderColor: '#0D9488',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #CBD5E1',
          padding: '14px 16px',
          color: '#0F172A',
          fontSize: '0.9rem',
          fontWeight: 500,
        },
        head: {
          background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
          color: '#0F172A',
          fontWeight: 800,
          fontSize: '0.82rem',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          borderBottom: '2px solid #CBD5E1',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 150ms ease-in-out',
          '&:hover': {
            background: '#F8FAFC',
          },
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 700,
          fontSize: '0.78rem',
          border: '1.5px solid #CBD5E1',
          background: '#F8FAFC',
          color: '#0F172A',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          background: '#FFFFFF',
          border: '1.5px solid #CBD5E1',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.15), 0 8px 16px rgba(15, 23, 42, 0.08)',
          color: '#0F172A',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#FFFFFF',
          borderRight: '1.5px solid #E2E8F0',
          boxShadow: '4px 0 16px rgba(15, 23, 42, 0.06)',
          color: '#0F172A',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          color: '#0F172A',
          boxShadow: '0 1px 0 #E2E8F0, 0 2px 8px rgba(15, 23, 42, 0.05)',
          borderBottom: '1.5px solid #E2E8F0',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#1E293B',
          color: '#F8FAFC',
          borderRadius: 8,
          fontSize: '0.78rem',
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)',
          padding: '6px 12px',
        },
        arrow: {
          color: '#1E293B',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E2E8F0',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 200ms ease',
          '&:hover': {
            backgroundColor: 'rgba(13, 148, 136, 0.08)',
          },
        },
      },
    },
  },
};

export const getAppTheme = (mode = 'dark') => {
  return createTheme(mode === 'light' ? lightThemeConfig : darkThemeConfig);
};

const defaultTheme = getAppTheme('dark');
export default defaultTheme;
